import { useReduxState } from 'hooks';
import { ArrayField } from 'informed';
import { Button } from 'components/common';
import {
  DisplayField,
  FormGroup,
  LanguageVariants,
  Text,
  TextArea
} from 'components/common/form';
import { breakpoints } from 'styles';

interface Props {
  field?: string;
  marginTop?: number;
}

const CompetenciesFormLevels = ({
  field: fieldName = 'levels',
  marginTop = 20
}: Props) => {
  const { messages, theme } = useReduxState();

  return (
    <ArrayField field={fieldName}>
      {({ add, swap, fields }) => (
        <div className="levels">
          <ArrayField.Items>
            {({ field, index, remove }) => (
              <div className="level">
                <DisplayField field={`${field}.id`} />

                <div className="inner">
                  <div className="content">
                    <div className="number">
                      <span className="number-text">
                        {`${messages.commonLevel} ${index + 1}`}
                      </span>

                      {fields.length > 1 &&
                      (index === 0 || index === fields.length - 1) ? (
                        <span className="number-help-text">
                          {`(${
                            index === 0
                              ? messages.commonLowest
                              : messages.commonHighest
                          })`}
                        </span>
                      ) : null}
                    </div>

                    <div className="item">
                      <div className="title">
                        <FormGroup label={messages.commonTitle}>
                          <Text
                            field={`${field}.name`}
                            id={`${field}-name`}
                            required={true}
                            validateOnBlur={true}
                            validateOnChange={true}
                          />

                          <LanguageVariants
                            field={`${field}.nameLocales`}
                            type="text"
                          />
                        </FormGroup>
                      </div>

                      <div className="description">
                        <FormGroup label={messages.commonDescription}>
                          <TextArea
                            field={`${field}.description`}
                            id={`${field}-description`}
                          />

                          <LanguageVariants
                            field={`${field}.descriptionLocales`}
                            type="textarea"
                          />
                        </FormGroup>
                      </div>
                    </div>
                  </div>

                  <div className="actions">
                    <Button
                      category="light"
                      disabled={index === 0}
                      icon="arrow-up"
                      isRounded={true}
                      onClick={() => swap(index, index - 1)}
                      size="small"
                      tooltip={messages.commonMoveUp}
                    />

                    <Button
                      category="light"
                      disabled={index === fields.length - 1}
                      icon="arrow-down"
                      isRounded={true}
                      onClick={() => swap(index, index + 1)}
                      size="small"
                      tooltip={messages.commonMoveDown}
                    />

                    <Button
                      category="light"
                      disabled={fields.length === 1}
                      icon="trash-alt"
                      isRounded={true}
                      onClick={remove}
                      size="small"
                      tooltip={messages.commonRemove}
                    />
                  </div>
                </div>
              </div>
            )}
          </ArrayField.Items>

          <div className="footer">
            <Button
              category="lighter"
              icon="plus"
              onClick={add}
              size="medium"
              text={messages.competenciesLevelsAdd}
            />
          </div>

          <style jsx>{`
            .levels {
              display: flex;
              flex-direction: column;
              gap: 20px;
              margin-top: ${marginTop}px;
            }
            .level {
              background-color: ${theme.lightGreyLighter};
              border-radius: ${theme.cardBorderRadius};
              padding: 12px 15px;
            }
            .inner {
              display: flex;
              gap: 15px;
            }
            .number {
              margin-bottom: 10px;
            }
            .number-text {
              color: ${theme.textColorLight};
              font-size: 1.5rem;
              font-weight: 700;
              margin-right: 7px;
            }
            .number-help-text {
              color: ${theme.textColorLighter};
              font-size: 1.4rem;
              font-weight: 400;
            }
            .content {
              flex-grow: 1;
            }
            .item {
              display: flex;
              flex-direction: column;
              flex-grow: 1;
              gap: 15px 20px;
            }
            .actions {
              align-items: center;
              display: flex;
              flex-direction: column;
              gap: 5px;
              padding-top: 5px;
              padding-left: 20px;
            }
            @media (min-width: ${breakpoints.large}px) {
              .item {
                flex-direction: row;
              }
              .title {
                width: 45%;
              }
              .description {
                flex-grow: 1;
              }
            }
          `}</style>
        </div>
      )}
    </ArrayField>
  );
};

export default CompetenciesFormLevels;
